import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import sessionsReportApi, {
  type SessionsReportFiltersI,
  type SessionsReportResponseI,
} from 'Api/axios/sessions-report-api';
import { downloadFile } from 'Utils/file-utils';
import useTranslation from 'Hooks/useTranslation';
import type { SessionsReportFilterValues } from '../schemas/sessions-report-schemas';

const toApiFilters = (values: SessionsReportFilterValues): SessionsReportFiltersI => {
  const { search, startDate, endDate, ...rest } = values;
  return {
    ...rest,
    search: search?.trim() || undefined,
    startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
  };
};

const useSessionsReportPage = ({ initialUserId }: { initialUserId?: string } = {}) => {
  const { t2 } = useTranslation('SessionsReport');
  const [report, setReport] = useState<SessionsReportResponseI | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<SessionsReportFilterValues | null>(null);

  const fetchReport = useCallback(async (values: SessionsReportFilterValues) => {
    const apiFilters = toApiFilters(values);
    if (initialUserId) apiFilters.userId = initialUserId;
    setCurrentFilters(values);
    setLoading(true);
    setError(null);
    const response = await sessionsReportApi.fetchReport(apiFilters);
    if (response.error) {
      setError(response.error);
      setReport(null);
    } else {
      setReport(response.data || null);
    }
    setLoading(false);
    return response;
  }, [initialUserId]);

  // No auto-fetch here — the filter form fires the first fetch on mount via
  // its own effect (so the period preset is applied before the call).

  const handleExport = useCallback(async () => {
    if (!currentFilters) return;
    setExporting(true);
    const response = await sessionsReportApi.exportReport(toApiFilters({
      ...currentFilters,
      ...(initialUserId ? { userId: initialUserId } : {}),
    }));
    setExporting(false);
    if (response.error) {
      toast.error(response.error);
      return;
    }
    if (response.data) {
      downloadFile(response.data, `reporte-sesiones-${Date.now()}.xlsx`);
      toast.success(t2('toasts.reportExported'));
    }
  }, [currentFilters, initialUserId, t2]);

  return {
    report,
    loading,
    error,
    exporting,
    fetchReport,
    handleExport,
    t2,
  };
};

export default useSessionsReportPage;
